import {
	DataSource,
	type DataSourceDataParams,
	type DataSourceLivePaginationCursorFn,
	InfiniteTable,
	type InfiniteTablePropColumns,
} from "@infinite-table/infinite-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { BacktestItem } from "epsm-api-types/models";
import { useCallback, useEffect, useState } from "react";
import { getFilterParams } from "@/components/widgets/Backtest/helpers";
import { useFetchNextPage } from "@/components/widgets/Backtest/hooks/useFetchNextPage.ts";
import { useSetColumns } from "@/components/widgets/Backtest/hooks/useSetColumns.ts";
import type { IBacktestConfig } from "@/components/widgets/Backtest/types.ts";
import { BacktestSettingsSelector } from "@/components/widgets/Backtest/ui/BacktestSettingsSelector.tsx";
import { INFINITY_TABLE } from "@/config.ts";
import { getParams } from "@/lib/utils";
import backtestConfig from "../config";
import { UseData } from "../services/UseData";
import "./Backtest.css";
import {
	getDrawdownOptions,
	getPNLOptions,
} from "@/components/widgets/Backtest/helpers";
import { BacktestChart } from "@/components/widgets/Backtest/ui/BacktestChart";
import { settings } from "@/lib/utils/settings/settings";

const {
	defaultSortInfo,
	DEFAULT_FILTER_DELAY,
	DEFAULT_COLUMN_ORDER,
	DEFAULT_PAGE_SIZE,
	COLUMN_DEFAULT_FLEX,
} = backtestConfig;

let totalCount = 0;

export const Backtest = () => {
	const { execute, isLoading } = UseData.create().useGetBacktestData();

	const [selectedBacktest, setSelectedBacktest] = useState<IBacktestConfig>();

	const [columnOrder, setColumnOrder] =
		useState<string[]>(DEFAULT_COLUMN_ORDER);
	const [columns, setColumns] = useState<
		InfiniteTablePropColumns<BacktestItem>
	>({});

	const getData = async (dataInfo: DataSourceDataParams<BacktestItem>) => {
		const result = await execute({
			params: {
				...getParams(
					dataInfo,
					columnOrder.length ? columnOrder : undefined,
					DEFAULT_PAGE_SIZE,
				),
				fields: DEFAULT_COLUMN_ORDER,
				filter: getFilterParams(selectedBacktest?._id || ""),
				types: { date: "date" },
			},
		});
		if (result?.metadata?.total) {
			totalCount = result.metadata.total;
		}

		return {
			data: result?.data || [],
			hasMore: result?.metadata?.pagination?.nextPage,
			totalCount,
		};
	};

	const [dataParams, setDataParams] = useState<
		Partial<DataSourceDataParams<BacktestItem>>
	>({
		sortInfo: defaultSortInfo,
		livePaginationCursor: 0,
	});

	const { data, fetchNextPage, isFetchingNextPage, hasNextPage } =
		useInfiniteQuery({
			queryKey: [
				"BacktestData",
				columnOrder,
				selectedBacktest,
				dataParams.sortInfo,
			],
			queryFn: async ({ pageParam = 0 }) => {
				if (!selectedBacktest) {
					return { hasMore: false, data: [], totalCount: 0 };
				}
				const params: DataSourceDataParams<BacktestItem> = {
					originalDataArray: [],
					livePaginationCursor: pageParam,
					sortInfo: dataParams.sortInfo,
				};
				return await getData(params);
			},
			initialPageParam: 0,
			getNextPageParam: (lastPage, allPages) => {
				if (lastPage?.hasMore) {
					return (allPages?.length ?? 0) * DEFAULT_PAGE_SIZE;
				}
				return undefined;
			},
			select: (data) => {
				const flatData = data.pages.flatMap((x) => x.data);
				const result = {
					pages: flatData,
				};
				return result;
			},
		});

	const livePaginationCursorFn: DataSourceLivePaginationCursorFn<BacktestItem> =
		useCallback(({ length }) => {
			return length;
		}, []);

	useSetColumns(setColumnOrder, setColumns);
	useFetchNextPage(dataParams, isFetchingNextPage, isLoading, fetchNextPage);

	const onDataParamsChange = useCallback(
		(dataParams: DataSourceDataParams<BacktestItem>) => {
			const params = {
				sortInfo: dataParams.sortInfo,
				livePaginationCursor: dataParams.livePaginationCursor,
			};
			setDataParams(params);
		},
		[],
	);

	useEffect(() => {
		if (hasNextPage) {
			fetchNextPage();
		}
	}, [hasNextPage, fetchNextPage]);

	return (
		<div className="h-full flex flex-col overflow-auto">
			<BacktestSettingsSelector
				selectedBacktest={selectedBacktest}
				setSelectedBacktest={setSelectedBacktest}
			/>
			<div className="flex-1">
				<div className="h-full flex flex-row overflow-auto">
					<BacktestChart
						data={data?.pages ?? []}
						getOptions={getPNLOptions}
						isLoading={isLoading || isFetchingNextPage}
					/>
					<BacktestChart
						data={data?.pages ?? []}
						getOptions={getDrawdownOptions}
						isLoading={isLoading || isFetchingNextPage}
					/>
				</div>
			</div>

			<div className="flex-1">
				<DataSource<BacktestItem>
					data={data?.pages ?? []}
					filterDelay={DEFAULT_FILTER_DELAY}
					primaryKey="date"
					defaultSortInfo={defaultSortInfo}
					loading={isLoading || isFetchingNextPage}
					livePagination={true}
					livePaginationCursor={livePaginationCursorFn}
					onDataParamsChange={onDataParamsChange}
				>
					<InfiniteTable<BacktestItem>
						columns={columns}
						columnOrder={columnOrder}
						onColumnOrderChange={setColumnOrder}
						rowHeight={INFINITY_TABLE.ROW_HEIGHT}
						columnHeaderHeight={INFINITY_TABLE.COLUMN_HEADER_HEIGHT}
						licenseKey={settings.infiniteTableLicenseKey}
						columnDefaultFlex={COLUMN_DEFAULT_FLEX}
					/>
				</DataSource>
			</div>
		</div>
	);
};
